/** @flow */
import React, { Fragment } from "react";
import { Checkbox, Button, Tooltip } from "../index";
import "./checkbox-group.style.scss";
import type { CheckboxGroupItem } from "./types";

type Props = $Exact<{
  data: CheckboxGroupItem[],
  maximum?: number,
  t: (textKey: string, params?: Array<string> | {}, comment?: string) => string,
  changesHandler: Function,
}>;

type ComponentState = $Exact<{
  data: CheckboxGroupItem[] | null,
  hoveredElement: string | null,
}>;

export class CheckboxGroup extends React.Component<Props, ComponentState> {
  state = {
    data: null,
    hoveredElement: null,
  };

  get dataSource() {
    return this.state.data || this.props.data;
  }

  handleChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState(
      {
        data: this.dataSource.map(item => {
          if (item.value === event.target.id) {
            return { ...item, checked: !item.checked };
          }
          return item;
        }),
      },
      () => this.props.changesHandler(this.state.data),
    );
  };

  get selectedLength() {
    return this.dataSource.filter(item => item.checked).length;
  }

  get selectedCounter() {
    if (!this.props.maximum) {
      return null;
    }
    if (!this.selectedLength) {
      return (
        <span className="text-negative font-weight-bold">
          {this.props.t("CHECKBOX_GROUP_SELECT_QUESTIONS", {
            count: this.props.maximum,
          })}
        </span>
      );
    }

    if (this.selectedLength === this.props.maximum) {
      return (
        <span className="text-positive font-weight-bold">
          {this.props.t("CHECKBOX_GROUP_ALL_QUESTIONS_SELECTED", {
            count: this.props.maximum,
          })}
        </span>
      );
    } else if (this.selectedLength < this.props.maximum) {
      const elementsLeft = this.props.maximum - this.selectedLength;

      return (
        <span className="text-negative font-weight-bold">
          {this.props.t("CHECKBOX_GROUP_SELECT_MORE", {
            count: elementsLeft,
          })}
        </span>
      );
    }
    return null;
  }

  handleClear = () => {
    this.setState(
      {
        data: this.dataSource.map(item => ({ ...item, checked: false })),
      },
      () => this.props.changesHandler(this.state.data),
    );
  };

  isReadOnly = (value: string) => {
    const isSelected = this.dataSource.some(item => item.value === value && item.checked);
    if (!isSelected) {
      return this.selectedLength === this.props.maximum;
    }
    return false;
  };

  toggleHovered = (value: string) => () => {
    const sameTarget = this.state.hoveredElement === value;
    const hoveredElement = sameTarget ? null : value;
    this.setState({ hoveredElement });
  };

  render() {
    return (
      <div className="d-flex flex-column checkbox-group-wrapper">
        {this.dataSource.map(item => (
          <Fragment key={item.value}>
            <Checkbox
              id={item.value}
              label={<div id={`tooltip${item.value}`}>{item.name}</div>}
              onChange={this.handleChange}
              disabled={this.isReadOnly(item.value)}
              checked={item.checked}
            />
            <Tooltip
              className="checkbox-group"
              placement="bottom-start"
              isOpen={this.state.hoveredElement === item.value && this.isReadOnly(item.value)}
              target={`tooltip${item.value}`}
              toggle={this.toggleHovered(item.value)}
            >
              {this.props.t("CHECKBOX_GROUP_TOOLTIP_DATA", { count: this.props.maximum })}
            </Tooltip>
          </Fragment>
        ))}
        <div className="d-flex flex-row align-items-center mt-3 bottom-section">
          {this.props.maximum ? (
            <div className="py-3 normal-font-size w-100">{this.selectedCounter}</div>
          ) : null}
          {this.selectedLength ? (
            <div className="w-100 d-flex justify-content-center">
              <Button size="small" onClick={this.handleClear}>
                {this.props.t("CHECKBOX_GROUP_CLEAR_SELECTION")}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default CheckboxGroup;
